import { DateTime } from "luxon";
import { TFunction } from "i18next";
import { VehiclePermissionAffectedBooking } from "../types/VehiclePermissions.types";
import { Colleague } from "../../../features/Connections/types/Colleague.type";

const RANGE_SEP = " – ";

/** Format ISO date-time with Luxon and locale format, or return "" if invalid */
export function formatIsoWithLocale(iso: string | undefined, dateTimeFormat: string): string {
  if (!iso) return "";
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat(dateTimeFormat) : "";
}

/** Combine start and end strings into a single range display string */
function formatRangeParts(start: string, end: string): string {
  if (start && end) return `${start}${RANGE_SEP}${end}`;
  if (start) return start;
  return end;
}

/** Default date-time format when none provided (ISO start/end) */
const DEFAULT_DATETIME_FORMAT = "yyyy-MM-dd HH:mm";

function formatBookingRangeFromIso(
  b: VehiclePermissionAffectedBooking,
  dateTimeFormat: string
): string | null {
  if (b.start == null && b.end == null) return null;
  const start = formatIsoWithLocale(b.start, dateTimeFormat);
  const end = formatIsoWithLocale(b.end, dateTimeFormat);
  return formatRangeParts(start, end) || null;
}

function formatBookingRangeFromDateParts(b: VehiclePermissionAffectedBooking): string | null {
  const start = b.startDate && b.startTime ? `${b.startDate} ${b.startTime}` : b.startDate ?? "";
  const end = b.endDate && b.endTime ? `${b.endDate} ${b.endTime}` : b.endDate ?? "";
  return formatRangeParts(start, end) || null;
}

export function formatBookingRange(
  b: VehiclePermissionAffectedBooking,
  t: TFunction<"translation">,
  dateTimeFormat?: string
): string {
  const fmt = dateTimeFormat ?? DEFAULT_DATETIME_FORMAT;
  const fromIso = formatBookingRangeFromIso(b, fmt);
  if (fromIso) return fromIso;
  const fromParts = formatBookingRangeFromDateParts(b);
  if (fromParts) return fromParts;
  return t("Booking") + (b.id == null ? "" : ` #${b.id}`);
}

type PersonLike = { firstName?: string; surname?: string; email?: string };

/** Display name from firstName/surname or email; null if nothing present */
function displayNameFromPerson(p: PersonLike | undefined): string | null {
  if (!p) return null;
  const name = [p.firstName, p.surname].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (p.email) return p.email;
  return null;
}

export function formatBookingUserName(
  b: VehiclePermissionAffectedBooking,
  t: TFunction<"translation">,
  allUsers?: Colleague[] | null
): string {
  const fromUserInfo = displayNameFromPerson(b.userInfo);
  if (fromUserInfo) return fromUserInfo;

  const userId = b.userBookedFor ?? b.userId;
  if (userId && allUsers?.length) {
    const user = allUsers.find(c => c.userId === userId || c.id === userId);
    const fromColleague = displayNameFromPerson(user);
    if (fromColleague) return fromColleague;
  }
  if (userId) return userId;
  return t("Booking") + (b.id == null ? "" : ` #${b.id}`);
}
